import type { RealtimeEvent } from '@/types/email';

/**
 * Generates a deduplication key for real-time events.
 * Events with the same key will replace each other in the queue.
 */
export function getEventDedupKey(event: RealtimeEvent): string | null {
  switch (event.type) {
    case 'mailbox.counts':
      // Key by the affected mailbox set, not just the account. These events are
      // PARTIAL (each carries only the mailboxes that just changed), so a bare
      // per-account key would let a later single-mailbox event replace an earlier
      // one for a different mailbox in the same burst — silently dropping that
      // mailbox's count update (the "sidebar only updates on refresh" symptom).
      // Including the sorted ids coalesces only truly-redundant repeats.
      return `mailbox.counts:${event.accountId}:${event.mailboxes
        .map((m) => m.id)
        .sort()
        .join(',')}`;
    case 'sync.status':
      return 'sync.status';
    case 'mailbox.sync.progress': {
      const { accountId, mailboxId } = event;
      if (accountId && mailboxId) {
        return `mailbox.sync.progress:${accountId}:${mailboxId}`;
      }
      if (accountId) {
        return `mailbox.sync.progress:${accountId}`;
      }
      return 'mailbox.sync.progress';
    }
    case 'email.received':
    case 'email.updated':
      return `${event.type}:${event.accountId}:${event.email.id}`;
    case 'email.moved':
      // Include the from→to folders (R4): a Gmail message lives in several
      // labels, so ONE message id can produce several DISTINCT label
      // transitions in a burst. A bare account:id key let a later transition
      // replace an earlier one in the hidden-tab queue, dropping exactly the
      // per-folder events the multi-label/stale-copy guards need. Only truly
      // redundant repeats (same from→to) still coalesce.
      return `email.moved:${event.accountId}:${event.emailId}:${event.fromFolderId ?? ''}:${event.toFolderId ?? ''}`;
    case 'email.deleted':
      // Include the folder (R4): a per-label delete must not replace a delete
      // for the same id in a different label within one burst.
      return `email.deleted:${event.accountId}:${event.emailId}:${event.folderId ?? ''}`;
    case 'folder.preferences.updated':
      return `folder.preferences.updated:${event.accountId}`;
    case 'attachment.downloaded':
      return `attachment.downloaded:${event.accountId}:${event.emailId}:${event.attachmentId}`;
    case 'sync.job.updated':
      return `sync.job.updated:${event.accountId}:${event.job.id}`;
    case 'resync':
      return 'resync';
    default:
      return null;
  }
}
